import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './contexts/AuthContext';
import { UploadProvider } from './contexts/UploadContext';
import { TopupProvider, useTopup } from './contexts/TopupContext';
import FloatingUploadBar from './components/FloatingUploadBar';
import TopupDialog from './components/TopupDialog';
import OpsBanner from './components/OpsBanner';
import Home from './pages/Home';
import RequireAuth from './components/RequireAuth';
import { Loader2 } from 'lucide-react';

const Callback    = lazy(() => import('./pages/Callback'));
const Dashboard   = lazy(() => import('./pages/Dashboard'));
const Jobs        = lazy(() => import('./pages/Jobs'));
const NewJob      = lazy(() => import('./pages/NewJob'));
const JobDetail   = lazy(() => import('./pages/JobDetail'));
const Master      = lazy(() => import('./pages/Master'));
const Collections      = lazy(() => import('./pages/Collections'));
const CollectionDetail = lazy(() => import('./pages/CollectionDetail'));
const CollectionNew    = lazy(() => import('./pages/CollectionNew'));
const Profile          = lazy(() => import('./pages/Profile'));
const ApiTokens        = lazy(() => import('./pages/ApiTokens'));
const ApiDocs          = lazy(() => import('./pages/ApiDocs'));
const PublicShare      = lazy(() => import('./pages/PublicShare'));
const RacePreview      = lazy(() => import('./pages/RacePreview'));
const TryPreview       = lazy(() => import('./pages/TryPreview'));
const PublicPreview    = lazy(() => import('./pages/PublicPreview'));
const Credits          = lazy(() => import('./pages/Credits'));
const About            = lazy(() => import('./pages/About'));

const Loading: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-space-950 text-white/50">
    <Loader2 className="h-5 w-5 animate-spin" />
  </div>
);

// BrowserRouter must wrap AuthProvider — AuthContext calls useNavigate() so
// the post-OAuth-callback redirect swaps the active route (not just the URL)
// and the <Callback> spinner unmounts cleanly.
// GlobalTopupDialog — mounted once at the app root so the credit-balance
// chip in the Header (any page) and the "Add credits" button on Profile
// share one dialog instance. Reads open/close from TopupContext.
const GlobalTopupDialog: React.FC = () => {
  const { isOpen, close } = useTopup();
  return <TopupDialog open={isOpen} onClose={close} />;
};

const App: React.FC = () => (
  <HelmetProvider>
    <BrowserRouter>
      <AuthProvider>
        <UploadProvider>
          <TopupProvider>
            <OpsBanner />
            <Suspense fallback={<Loading />}>
              <Routes>
                <Route path="/"          element={<Home />} />
                <Route path="/callback"  element={<Callback />} />
                <Route path="/s/:slug"   element={<PublicShare />} />
                <Route path="/try"          element={<TryPreview />} />
                <Route path="/preview/:id"  element={<PublicPreview />} />
                <Route path="/api-docs"     element={<ApiDocs />} />
                <Route path="/credits"      element={<Credits />} />
                <Route path="/about"        element={<About />} />
                <Route path="/dashboard"   element={<RequireAuth><Dashboard /></RequireAuth>} />
                <Route path="/jobs"        element={<RequireAuth><Jobs /></RequireAuth>} />
                <Route path="/jobs/new"    element={<RequireAuth><NewJob /></RequireAuth>} />
                <Route path="/jobs/:id"        element={<RequireAuth><JobDetail /></RequireAuth>} />
                <Route path="/jobs/:id/master" element={<RequireAuth><Master /></RequireAuth>} />
                <Route path="/collections"     element={<RequireAuth><Collections /></RequireAuth>} />
                <Route path="/collections/new" element={<RequireAuth><CollectionNew /></RequireAuth>} />
                <Route path="/collections/:id" element={<RequireAuth><CollectionDetail /></RequireAuth>} />
                <Route path="/profile"         element={<RequireAuth><Profile /></RequireAuth>} />
                <Route path="/profile/tokens"  element={<RequireAuth><ApiTokens /></RequireAuth>} />
                <Route path="/dev/race"        element={<RacePreview />} />
              </Routes>
            </Suspense>
            <FloatingUploadBar />
            <GlobalTopupDialog />
          </TopupProvider>
        </UploadProvider>
      </AuthProvider>
    </BrowserRouter>
  </HelmetProvider>
);

export default App;
